<template>
  <div class="field-input-radio">
    <div class="input-group">
      <div class="btn-group btn-group-toggle smaller-font-content select-images" data-toggle="buttons">
        <v-select
          :name="'typeahead_' + fieldData.field_name"
          :id="'typeahead_' + fieldData.field_name"
          :disabled ="!!(showDisabled())"
          v-bind:class="{'std-width form-control': false, 'error': errors.has(fieldData.field_name) || fieldData.has_error }"
          v-model="selectChosen"
          :options="selectOptions"
          maxHeight="370px"
          placeholder="Select One"
          size="auto">
              <template slot="option" slot-scope="option">
                <div class="option-container">
                    <div v-if="option.thumbnail" class="select-image-area">
                      <img :src="'/static/assets/interview/' + option.thumbnail" class="select-thumbnail" />
                    </div>
                    <div class="select-desc-area">
                      <strong>{{ option.label }}</strong>
                      <p v-if="option.description" class="select-description">{{ option.description }}</p>
                    </div>
                </div>
              </template>
          </v-select>

          <input
          v-show="false"
          :name="fieldData.field_name"
          :id="fieldData.field_name"
          :disabled ="!!(showDisabled())"
          type="text"
          class="form-control"
          :maxlength="fieldData.max_length"
          v-model="fieldData.field_value"
          v-validate="field_rules">
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import { inputFieldMixin } from '../../mixins/inputFieldMixin';
import { homeSelectDetails, condoSelectDetails, rentersSelectDetails } from '../../../config/dictionaries/select-images-details';

export default {
  inject: ['$validator'], // Allows this component's validation messages to appear in other components
  props: ['fieldData', 'inputType', 'sendPostRule'],
  mixins: [inputFieldMixin],
  data() {
    return {
      currentProductType: '',
      optionList: [], // Populated on `mounted` AND any time `fieldData.values` is updated (API request changes those values)
      optionChosen: { label: '', value: '', thumbnail: '', description: '' }, // Populated on `mounted` AND upon an option selected (triggered by the MODEL updating, aka `CHOSEN` aka `selectOptions`)
    };
  },
  methods: {
    // TODO: Abstract into helper class
    /**
     * The validation message overlaps the interactive form element, so it is cleared
     * once the components containing the element is clicked.
     */
    clearMessages() {
      this.fieldData.has_error = false; // Reset API errors to false upon any interaction
      this.errors.remove(this.fieldData.field_name); // Temporarily hide the validation message
    },
    /**
     * Uses imported data objects found in select-images-details.js which hold the details of dropdowns organized by product and question name.
     * Since rendering these select options are codependent between the FE and BE, product, question, and option values must be an exact match.
     */
    fillWithOptionDetails(questionProduct, questionFieldName, questionValues) {
      const productDictionary = {
        home: homeSelectDetails,
        condo: condoSelectDetails,
        renters: rentersSelectDetails,
      };

      const question = questionFieldName.split('_').pop();

      questionValues.forEach((option) => {
        if (productDictionary[questionProduct][question]
            && productDictionary[questionProduct][question][option.internal_value]
            && productDictionary[questionProduct][question][option.internal_value].item_thumbnail
            && productDictionary[questionProduct][question][option.internal_value].item_description) {
          option.item_thumbnail = productDictionary[questionProduct][question][option.internal_value].item_thumbnail;
          option.item_description = productDictionary[questionProduct][question][option.internal_value].item_description;
        } else {
          option.item_thumbnail = '';
          option.item_description = '';
        }
      });
    },
  },
  created() {
    // Finds the current product for this select question
    for (let i = this.$route.matched.length - 1; i >= 0; i -= 1) {
      if (this.$route.matched[i].meta.productType) {
        this.currentProductType = this.$route.matched[i].meta.productType;
      }
    }

    if (this.fieldData.field_type === 'select_long') {
      /**
       * Fills in fieldData.values with option descriptions and images to render
       * InputSelectImages options according to supplemental reference file select-images-details.js
       * Parameters are currentProductType, question, and options for that question.
       * This enables faster lookup in select-images-details.js.
       */
      this.fillWithOptionDetails(this.currentProductType, this.fieldData.field_name, this.fieldData.values);
      this.selectOptions = this.fieldData.values;
    }
  },
  computed: {
    /* eslint prefer-const: */
    /* eslint no-undef:  */
    /* eslint no-unused-vars: */
    selectOptions: { // aka OPTIONS
      get: function () {
        return this.optionList;
      },
      set: function (value) {
        this.optionList = []; // Reset list to empty
        const improperOptionList = this.fieldData.values;
        improperOptionList.forEach((val) => {
          const properOptionItem = {
            label: val.display_value,
            value: val.internal_value,
            thumbnail: val.item_thumbnail,
            description: val.item_description,
          };

          // Select the option that matches the API data's value
          if (properOptionItem.value === this.fieldData.field_value) {
            this.optionChosen = properOptionItem;
          }
          this.optionList.push(properOptionItem);
        });
      },
    },
    selectChosen: { // aka CHOSEN
    /* eslint no-param-reassign:  */
    /* eslint object-shorthand:  */
    /* eslint func-names: [0] */
      get: function () {
        if (this.optionChosen.value === '') {
          return false;
        }
        return this.optionChosen;
      },
      set: function (value = null) {
        if (!value) {
          value = {
            label: '',
            value: '',
          };
        }

        const isNewValue = (this.fieldData.field_value !== value.value);
        this.optionChosen = value;
        this.fieldData.field_value = value.value;

        // Send API request
        if (isNewValue && this.apiRule.present && this.apiRule.type === 'onchange') {
          this.postRule(this.fieldData.field_value, this.fieldData);
          this.fieldData.has_error = false; // Reset API errors to false upon any interaction
        }
      },
    },
    formdataValues: { // Needed to watch when `this.formdata.values` is updated
      get: function () {
        return this.fieldData.values;
      },
      set: function (value) {
        this.formdata.values = value;
      },
    },
  },
  components: {
    vSelect,
  },
  watch: {
    'fieldData.field_value': function fieldValue() {
      this.clearMessages();
    },
    formdataValues: function (newValue, oldValue) {
      this.optionList = []; // Reset list to empty
      this.optionChosen = {
        label: '',
        value: '',
        thumbnail: '',
        description: '',
      };

      let improperOptionList = this.fieldData.values;

      improperOptionList.forEach((val) => {
        let properOptionItem = {
          label: val.display_value,
          value: val.internal_value,
          thumbnail: val.item_thumbnail,
          description: val.item_description,
        };

        // Select the option that matches the API data's value
        if (properOptionItem.value === this.fieldData.field_value) {
          this.optionChosen = properOptionItem;
        }

        this.optionList.push(properOptionItem);
      });
    },
    /**
     * When visible updates from false -> true, set up Places Autocomplete, if necessary
     */
    is_visible: function (newValue, oldValue) {
      if (!oldValue && newValue) {
        this.places_autocomplete_init();
      }
    },
  },
};
</script>
<style>
.select-images .dropdown-menu {
  min-width: 450px;
  overflow-x: hidden;
  overflow-y: auto !important;
}
.select-images .selected-tag{
  max-width: 100% !important;
  overflow: hidden !important;
  white-space: nowrap !important;
}
.select-images .dropdown-menu li>a {
  padding: 0 !important;
}
.select-images .option-container {
  display: flex;
}
.select-images .dropdown-menu li:hover, .select-images .dropdown-menu li a:hover, .select-images .dropdown-menu li p:hover {
  color: #fff !important;
}
.select-images .option-container .select-image-area {
  margin: 0.7rem 0 0.7em 0.7rem;
  min-width: 0px !important;
  width: 15%;
}
.select-images .option-container .select-desc-area {
  margin: 0.6rem;
  min-width: 0px !important;
  width: 85%;
}
.select-images .option-container .select-image-area .select-thumbnail {
  width: 100%;
  box-sizing: border-box;
}
.select-images .option-container .select-desc-area p.select-description {
  white-space:normal;
  margin-bottom: 0;
}
</style>
<style scoped>
/*
  TODO: Add IE/Edge support by implementing the `label.btn` styles via JavaScript. Bootstrap adds classes
  'focus' and 'active' to the radio <input>'s "closest" element, which happens to be the <label>.
  See https://github.com/twbs/bootstrap/blob/7c7a1706c348fcb5290a63d9249e0c8e4b6ebbcf/js/src/button.js#L153 .
*/
label.btn:focus-within {
  color: #fff;
  background-color: #001a33;
  border-color: #001326;
  z-index: 1;
  -webkit-box-shadow: 0 0 0 0.2rem rgba(0, 51, 102, 0.5);
  box-shadow: 0 0 0 0.2rem rgba(0, 51, 102, 0.5);
  -webkit-box-shadow: 0 0 0 0.2rem rgba(0, 51, 102, 0.5);
  box-shadow: 0 0 0 0.2rem rgba(0, 51, 102, 0.5);
}

select.form-control {
    height: auto !important;
}
</style>



// WEBPACK FOOTER //
// src/components/interactive/InputSelectImages.vue?503ad6d2